import { Form, Table } from 'react-bootstrap';
import { UseFormReturn } from 'react-hook-form';
import { BookingForPayBalanceQuery } from '../../../types/graphqlOperations';
import PriceFormat from '../../utilities/PriceFormat';
import { PayBalanceOnSubmitValues } from '../PayBalanceForm';

/**
 * Sum the booking spaces totals
 *
 * The total is only given by spaces, so this sums the total for the full booking
 */
export function sumTotal(booking: BookingForPayBalanceQuery['booking']) {
  return booking.spaces.reduce(
    (prev, curr) => prev + curr.guestTotal.amountZD,
    0,
  );
}

/**
 * Sum the booking spaces balances
 *
 * Note that any cancelled spaces will have a balance of 0, so we don't need to check separately if each space has been
 * cancelled.
 *
 */
export function sumBalance(booking: BookingForPayBalanceQuery['booking']) {
  return booking.spaces.reduce(
    (prev, curr) => prev + curr.guestBalance.amountZD,
    0,
  );
}

/**
 * Pay Balance Prices
 *
 * Shows the total, amount paid so far and balance due. Sets the balance due to a hidden form field.
 */
export function PayBalancePrices({ booking, register }: PayBalancePricesProps) {
  // Calculate amounts
  const total = sumTotal(booking);
  const balance = sumBalance(booking);
  const paid = total - balance;
  const { currency } = booking.spaces[0].guestTotal;

  return (
    <>
      <Table>
        <tbody>
          <tr>
            <th>Total</th>
            <td>
              <PriceFormat amount={total} currencyIso={currency} />
            </td>
          </tr>
          <tr>
            <th>Paid so far</th>
            <td>
              - <PriceFormat amount={paid} currencyIso={currency} />
            </td>
          </tr>
          <tr className="fw-bold">
            <th>Balance due</th>
            <td>
              <PriceFormat amount={balance} currencyIso={currency} />
            </td>
          </tr>
        </tbody>
      </Table>
      {/* Hidden form fields */}
      <Form.Control
        readOnly
        {...register('amount.amountZD')}
        style={{ display: 'none' }}
        type="number"
        value={balance}
      />
      <Form.Control
        readOnly
        {...register('amount.currency')}
        style={{ display: 'none' }}
        value={currency}
      />
    </>
  );
}

export interface PayBalancePricesProps {
  booking: BookingForPayBalanceQuery['booking'];
  register: UseFormReturn<PayBalanceOnSubmitValues>['register'];
}

/**
 * Helper interface for `onSubmit` handler
 *
 * Note HTML inputs all return strings not numbers!
 */
export interface PayBalancePricesSubmitValues {
  amountZD: string;
  currency: string;
}
